import React, { useEffect, useMemo, useState } from 'react';
import { formatTFN, getBaseUrl, getTFN } from '../../common/common';
import { EECCheckout, pageLoad } from '../../common/GTMScripts';
import {
  useBusinessAddr,
  useCallCenterFlag,
  useCartData,
  useLoading,
  useServiceablitityData,
  useSessionData,
} from '../../hooks/order';
import { cartLogo, earthlinkLogo } from '../../images/index';
import { routesConfig, WHI_TFN } from '../../util/util';
import FixedCardFooter from './FixedCardFooter';
import LanguageToggle from './LanguageToggle';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import useTrackedTfn from '../../hooks/useTrackedTfn';
import useDisplayedTfnTracking from '../../hooks/useDisplayedTfnTracking';
import { getCurrentTfn, filterFixedWirelessProducts } from '../../helpers/tfn';

const HeaderMain = () => {
  const location = useLocation();
  const { productCount } = useCartData();
  const { sessionResponse, TFN, customTFN, referrer } = useSessionData();
  const { isInternetAdded, internetPlan, wirelessCartInfo, vasPlan, productsWith12MonthFixWirelessServCode } =
    useCartData();
  const [showCart, setShowCart] = useState(true);
  const [tfn, setTfn] = useState(TFN ? formatTFN(TFN) : getTFN());
  const { loading, scheduleLoading } = useLoading();
  const pathname =
    '/' +
    window?.location?.href.substring(window.location.href.lastIndexOf('/') + 1);
  const showFooter =
    pathname?.includes(routesConfig?.products) ||
    pathname?.includes(routesConfig?.vasPage) ||
    pathname?.includes('servRef');
  const childRef = React.createRef();
  const { showCallNow, showPromoStickers } = useCallCenterFlag();
  const { serviceabilityCheck } = useServiceablitityData();
  const { isBusinessAddress } = useBusinessAddr();
  const { fixedWirelessProducts: rawFixedWirelessProducts = [], plans = [], fixedWireless } = serviceabilityCheck;
  const isWinterPromo = true;
  const fixedWirelessProducts = useMemo(
    () => filterFixedWirelessProducts(rawFixedWirelessProducts, fixedWireless),
    [rawFixedWirelessProducts, fixedWireless],
  );

  const { addr = '', referrer: referrerQuery = '' } = queryString.parse(location.search);
  const referrerUri = (referrerQuery || referrer || '')?.toString();

  useEffect(() => {
    if (loading) {
      document.body.classList.add('body-loading');
    } else {
      document.body.classList.remove('body-loading');
    }
  }, [loading]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const HLPlan = isInternetAdded ? [internetPlan] : [];
    const allProducts = [
      ...HLPlan,
      ...wirelessCartInfo.wirelessPlanInfo,
      ...vasPlan,
    ];
    if (sessionResponse && !window.location.href.includes('addr')) {
      pageLoad(sessionResponse.uid);
    }
    if (productCount > 0) {
      EECCheckout(allProducts);
    }
    if (
      window?.location?.href.includes(routesConfig?.thanks) ||
      window?.location?.href.includes(routesConfig?.checkout) ||
      // ELNKWEB-3210: nothing on the not-serviceable page is purchasable, and the mockup has
      // no cart. Leaving it visible would also carry a count over from a previously
      // serviceable address, inviting the customer back into a cart that no longer applies.
      window?.location?.href.includes(routesConfig?.nonServiceable)
    ) {
      setShowCart(false);
    } else {
      setShowCart(true);
    }
    if (window.location.href.includes(routesConfig.products)) {
      document.body.classList.add('product-page');
    } else {
      document.body.classList.remove('product-page');
    }
  }, [sessionResponse, window.location.href]);

  useEffect(() => {
    if (TFN) {
      setTfn(formatTFN(TFN));
    }
  }, [TFN, window.location.href]);

  const customheadertfn = () => getCurrentTfn({
    referrerUri,
    fixedWirelessProducts,
    plans,
    productsWith12MonthFixWirelessServCode,
    isBusinessAddress,
    customTFN,
    fallbackTfn: tfn,
  });

  // Derived from the router rather than `window.location.href` so the gates below
  // re-evaluate on navigation instead of only when the header happens to
  // re-render for some other reason. `pathname` above is still href derived and
  // only feeds `showFooter`.
  const routePathname = location.pathname;

  // ELNKWEB-3210: the not-serviceable results page must render exactly one
  // call-tracking number across the nav, every card CTA and the promo button. The
  // header resolves its number independently of the cards, and getCurrentTfn keys
  // off the serviceable product lists - which are empty here, so it would fall
  // through to whatever customTFN/fallbackTfn happens to hold and disagree with the
  // cards. Pin it to the same number the page's CTAs use.
  const headerPhoneNumber =
    routePathname === routesConfig.nonServiceable
      ? formatTFN(WHI_TFN)
      : customheadertfn();
  const isInitialServiceabilityPage =
    routePathname === routesConfig.home && addr !== '';
  const shouldShowHeaderTfn =
    !isInitialServiceabilityPage &&
    !loading &&
    routePathname !== routesConfig.thanks &&
    Boolean(headerPhoneNumber);

  // Read the tracked number straight from Invoca rather than watching for the
  // DOM swap. The header TFN is withheld until serviceability resolves, so it
  // renders after Invoca's one-shot pass and is never swapped in place - but the
  // assignment is still recorded, so it can simply be read. useTrackedTfn also
  // remembers each base's number for the rest of the session (ELNKWEB-3321), so
  // returning to a base number seen earlier (e.g. address A -> B -> A, fiber ->
  // 5G -> fiber) restores it even after a later reswap made Invoca forget it.
  const headerDisplayNumber = useTrackedTfn(shouldShowHeaderTfn ? headerPhoneNumber : '');
  const headerTrackedTfn =
    headerDisplayNumber && headerDisplayNumber !== headerPhoneNumber
      ? headerDisplayNumber
      : '';

  // Record what the customer is actually looking at. The header is the one
  // surface that resolves the number for the whole page, so it is the only place
  // this belongs - a second caller would race it (ELNKWEB-3218).
  useDisplayedTfnTracking(headerDisplayNumber);

  const handleCartClick = () => {
    childRef.current.cartClick();
    document.body.classList.add('cart-clicked');
  };

  const filteredPlans = useMemo(() => {
    return plans?.filter(
      (item) =>
        item.downstreamSpd === '300000' ||
        item.downstreamSpd === '1000000' ||
        item.downstreamSpd === '5000000',
    );
  }, [plans]);
  return (
    <>
      {showFooter &&
        !scheduleLoading &&
        Object.keys(sessionResponse).length > 0 && (
        <div>
          <FixedCardFooter ref={childRef} />
        </div>
      )}

      <div
        className={`outer-header ${
          showCallNow || showPromoStickers ? 'agent_online_header' : ''
        }`}
      >
        <header>
          <div className="elink-container">
            <div className="d-flex j-between al-center">
              <div className="logo">
                <a href={getBaseUrl()}>
                  <img src={earthlinkLogo} alt="Earthlink Logo" />
                </a>
              </div>

              <div className="gift-card-section">
                {isWinterPromo ? (
                  ''
                ) : filteredPlans?.length > 0 && pathname === '/products' ? (
                  <div>
                    Use code <span>FIBER24</span> to receive a $200 gift card**
                  </div>
                ) : null}
              </div>

              <div className="d-flex al-center">
                <LanguageToggle />
                {shouldShowHeaderTfn ? (
                  <div className="header-tfn d-flex al-center">
                    <a
                      className="elnk-tfn-link"
                      href={`tel:${headerDisplayNumber}`}
                      data-ctc="header"
                    >
                      <i className="fa-solid fa-mobile-screen-button"></i>
                      <span
                        className="phone-span elnk-tfn-tag"
                        data-original-tfn={headerPhoneNumber || ''}
                        data-invoca-tfn={headerTrackedTfn}
                      >
                        {headerDisplayNumber}
                      </span>
                    </a>
                  </div>
                ) : null}
                {/* {showCart && <div className="header-cart d-flex al-center" onClick={()=>{handleCartClick();}}> */}
                {showCart && (
                  <button
                    className="header-cart button-cart d-flex al-center"
                    onClick={() => {
                      handleCartClick();
                    }}
                    onKeyDown={() => {}}
                  >
                    <img src={cartLogo} alt="cart-logo" />
                    <span>{productCount}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>
      </div>
    </>
  );
};

export default HeaderMain;
